import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

// Flatten nested user/project data onto the check-in record
function flattenCheckIn(ci) {
  if (!ci) return ci;
  const { user, project, ...rest } = ci;
  return {
    ...rest,
    first_name: user?.first_name || rest.first_name,
    last_name: user?.last_name || rest.last_name,
    display_name: user?.display_name || rest.display_name,
    avatar_url: user?.avatar_url || rest.avatar_url,
    department: user?.department || rest.department,
    project_name: project?.name || rest.project_name,
  };
}

/**
 * Custom hook for check-in page data using optimized RPCs
 * Handles initial load, gap analysis, and employee stats batching
 *
 * @param {string} projectId - The project ID (for single project view)
 * @param {object} options - Configuration options
 * @param {string[]} options.projectIds - Project IDs for stack view
 * @param {boolean} options.isStackView - Whether this is a stack view
 * @param {string} options.selectedSpaceFilter - Selected space filter for stack view
 * @param {string} options.selectedDepartment - Selected department filter
 * @param {boolean} options.enabled - Whether to fetch data
 * @returns {object} All check-in page data and methods
 */
export const useCheckInData = (projectId, options = {}) => {
  const {
    projectIds = null,
    isStackView = false,
    selectedSpaceFilter = 'all',
    selectedDepartment = null,
    enabled = true
  } = options;

  // Core data state
  const [project, setProject] = useState(null);
  const [siteLocation, setSiteLocation] = useState(null);
  const [allOnSiteUsers, setAllOnSiteUsers] = useState([]);
  const [recentCheckIns, setRecentCheckIns] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [companyId, setCompanyId] = useState(null);

  // Break events state (for activity feed)
  const [breakEvents, setBreakEvents] = useState([]);

  // Gap analysis state
  const [gapAnalysis, setGapAnalysis] = useState([]);
  const [gapSummary, setGapSummary] = useState({ total: 0, met: 0, excess: 0, shortage: 0 });

  // Employee stats state (batch loaded)
  const [employeeStats, setEmployeeStats] = useState({});

  // Loading states
  const [initialLoading, setInitialLoading] = useState(true);
  const [gapLoading, setGapLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);

  // Refs
  const initialLoadDone = useRef(false);
  const loadingRef = useRef(false);
  const prevProjectIdRef = useRef(projectId);
  const prevSpaceFilterRef = useRef(selectedSpaceFilter);
  const prevDepartmentRef = useRef(selectedDepartment);
  const allOnSiteUsersRef = useRef(allOnSiteUsers);

  // Keep ref in sync with state
  allOnSiteUsersRef.current = allOnSiteUsers;

  // Get effective project IDs based on filters
  const getEffectiveProjectIds = useCallback(() => {
    if (!isStackView) return null;
    if (selectedSpaceFilter === 'all') return projectIds;
    return [selectedSpaceFilter];
  }, [isStackView, selectedSpaceFilter, projectIds]);

  // Derived: filtered on-site users by department
  const onSiteUsers = useMemo(() => {
    if (!selectedDepartment) return allOnSiteUsers;
    return allOnSiteUsers.filter(checkIn =>
      checkIn.user?.department === selectedDepartment || checkIn.department === selectedDepartment
    );
  }, [allOnSiteUsers, selectedDepartment]);

  // Derived: filtered recent check-ins by department
  const filteredCheckIns = useMemo(() => {
    if (!selectedDepartment) return recentCheckIns;
    return recentCheckIns.filter(checkIn =>
      checkIn.user?.department === selectedDepartment || checkIn.department === selectedDepartment
    );
  }, [recentCheckIns, selectedDepartment]);

  // Derived: unified activity feed (check-ins + breaks merged chronologically)
  const activityFeed = useMemo(() => {
    const entries = [];

    // Add check-in/out events from recentCheckIns
    for (const ci of recentCheckIns) {
      // Always add a clock-in entry
      entries.push({
        id: `${ci.id}-in`,
        type: 'clock_in',
        time: ci.check_in_time,
        user_id: ci.user_id,
        first_name: ci.first_name,
        last_name: ci.last_name,
        display_name: ci.display_name,
        avatar_url: ci.avatar_url,
        check_in_method: ci.check_in_method,
        selfie_url: ci.selfie_url,
      });

      // Add clock-out entry if checked out
      if (ci.check_out_time) {
        entries.push({
          id: `${ci.id}-out`,
          type: 'clock_out',
          time: ci.check_out_time,
          user_id: ci.user_id,
          first_name: ci.first_name,
          last_name: ci.last_name,
          display_name: ci.display_name,
          avatar_url: ci.avatar_url,
          check_in_method: ci.check_in_method,
          check_in_time: ci.check_in_time,
          check_out_time: ci.check_out_time,
        });
      }
    }

    // Add break events
    for (const b of breakEvents) {
      entries.push({
        id: `${b.id}-break-start`,
        type: 'break_start',
        time: b.start_time,
        user_id: b.user_id,
        first_name: b.first_name,
        last_name: b.last_name,
        display_name: b.display_name,
        avatar_url: b.avatar_url,
      });

      if (b.end_time) {
        entries.push({
          id: `${b.id}-break-end`,
          type: 'break_end',
          time: b.end_time,
          user_id: b.user_id,
          first_name: b.first_name,
          last_name: b.last_name,
          display_name: b.display_name,
          avatar_url: b.avatar_url,
          duration_minutes: b.duration_minutes,
        });
      }
    }

    // Sort by time descending (most recent first)
    entries.sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());

    return entries.slice(0, 30);
  }, [recentCheckIns, breakEvents]);

  // Derived: stats (computed from data, no query needed!)
  const stats = useMemo(() => ({
    onSiteNow: onSiteUsers.length
  }), [onSiteUsers.length]);

  // Derived: total employee count and on-site count
  const totalEmployees = useMemo(() => {
    return departments.reduce((sum, d) => sum + (d.employeeCount || 0), 0);
  }, [departments]);

  const totalOnSite = useMemo(() => {
    return allOnSiteUsers.length;
  }, [allOnSiteUsers.length]);

  // Get department employee count
  const getDepartmentEmployeeCount = useCallback((dept) => {
    const deptData = departments.find(d => d.name === dept);
    return deptData?.employeeCount || 0;
  }, [departments]);

  // Get department on-site count
  const getDepartmentOnSiteCount = useCallback((dept) => {
    return allOnSiteUsers.filter(checkIn =>
      checkIn.user?.department === dept || checkIn.department === dept
    ).length;
  }, [allOnSiteUsers]);

  // Process analytics data from RPC response
  const processAnalytics = useCallback((rawAnalytics) => {
    if (!rawAnalytics || rawAnalytics.totalCheckIns === 0) {
      return null;
    }

    const completedCheckIns = rawAnalytics.completedCheckIns || 0;
    const totalDurationMs = rawAnalytics.totalDurationMs || 0;
    const avgDurationMs = completedCheckIns > 0 ? totalDurationMs / completedCheckIns : 0;

    // Format duration
    const formatTime = (ms) => {
      const hours = Math.floor(ms / (1000 * 60 * 60));
      const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
      return `${hours}h ${minutes}m`;
    };

    // Build last 7 days data
    const last7Days = [];
    const checkInsByDay = rawAnalytics.checkInsByDay || [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      date.setHours(0, 0, 0, 0);

      const dayStr = date.toISOString().split('T')[0];
      const dayData = checkInsByDay.find(d => d.date?.startsWith(dayStr));

      last7Days.push({
        date: date.toLocaleDateString('en-US', { weekday: 'short' }),
        fullDate: date,
        count: dayData?.count || 0
      });
    }

    // Process top hours
    const topHours = (rawAnalytics.checkInsByHour || []).map(h => ({
      hour: h.hour,
      count: h.count,
      label: h.hour === 0 ? '12am' :
             h.hour < 12 ? `${h.hour}am` :
             h.hour === 12 ? '12pm' :
             `${h.hour - 12}pm`
    }));

    // Find peak hour
    const peakHour = topHours.length > 0 ? topHours[0].label : 'N/A';

    return {
      last7Days,
      topHours,
      peakHour,
      totalCheckIns: rawAnalytics.totalCheckIns,
      completedCheckIns,
      totalHours: Math.floor(totalDurationMs / (1000 * 60 * 60)),
      avgDuration: formatTime(avgDurationMs)
    };
  }, []);

  // Initial page load - fetches everything in one call
  const fetchInitialData = useCallback(async () => {
    if (!enabled) return;
    if (!projectId && !isStackView) return;
    if (loadingRef.current) return;

    loadingRef.current = true;
    setInitialLoading(true);

    try {
      // Compute effective IDs inline to avoid dependency on getEffectiveProjectIds
      let effectiveIds = null;
      if (isStackView) {
        effectiveIds = selectedSpaceFilter === 'all' ? projectIds : [selectedSpaceFilter];
      }

      const { data, error } = await supabase.rpc('get_checkin_page_data', {
        p_project_id: projectId,
        p_project_ids: effectiveIds,
        p_is_stack_view: isStackView
      });

      if (error) throw error;

      setProject(data.project);
      setSiteLocation(data.siteLocation);
      setAllOnSiteUsers((data.onSiteUsers || []).map(flattenCheckIn));
      setRecentCheckIns((data.recentCheckIns || []).map(flattenCheckIn));
      setDepartments(data.departments || []);
      setAnalyticsData(processAnalytics(data.analytics));
      setCompanyId(data.companyId);

      initialLoadDone.current = true;

      // Fetch break events for activity feed (non-blocking)
      fetchBreakEvents();

      // Fetch gap analysis if we have on-site users (non-blocking)
      if (!isStackView && projectId && data.onSiteUsers?.length > 0) {
        fetchGapAnalysis(data.onSiteUsers.map(u => u.user_id));
      }

      // Batch load employee stats for all on-site users (non-blocking)
      if (!isStackView && projectId && data.onSiteUsers?.length > 0) {
        fetchEmployeeStatsBatch(data.onSiteUsers.map(u => u.user_id));
      }
    } catch (err) {
      console.error('Error fetching check-in page data:', err);
      toast.error('Failed to load check-in data');
    } finally {
      setInitialLoading(false);
      loadingRef.current = false;
    }
    // Note: projectIds and selectedSpaceFilter are used inline but we don't want them
    // to trigger re-creation of this callback - the useEffect handles those changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, isStackView, enabled, processAnalytics]);

  // Fetch gap analysis using optimized RPC
  const fetchGapAnalysis = useCallback(async (onSiteUserIds = null) => {
    if (!projectId || isStackView) {
      setGapAnalysis([]);
      setGapSummary({ total: 0, met: 0, excess: 0, shortage: 0 });
      return;
    }

    setGapLoading(true);

    try {
      // Use ref to get current on-site users without causing callback recreation
      const userIds = onSiteUserIds || allOnSiteUsersRef.current.map(u => u.user_id);

      const { data, error } = await supabase.rpc('get_checkin_gap_analysis', {
        p_project_id: projectId,
        p_on_site_user_ids: userIds,
        p_department: selectedDepartment
      });

      if (error) throw error;

      setGapAnalysis(data.gaps || []);
      setGapSummary(data.summary || { total: 0, met: 0, excess: 0, shortage: 0 });
    } catch (err) {
      console.error('Error fetching gap analysis:', err);
      setGapAnalysis([]);
      setGapSummary({ total: 0, met: 0, excess: 0, shortage: 0 });
    } finally {
      setGapLoading(false);
    }
  }, [projectId, isStackView, selectedDepartment]);

  // Fetch today's break events for activity feed
  const fetchBreakEvents = useCallback(async () => {
    if (!projectId && !isStackView) return;

    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      let effectiveIds = null;
      if (isStackView) {
        effectiveIds = selectedSpaceFilter === 'all' ? projectIds : [selectedSpaceFilter];
      }
      const projectIdArray = isStackView ? effectiveIds : [projectId];

      // Step 1: Get today's checkin IDs for this project
      const { data: todayCheckins, error: checkinError } = await supabase
        .from('site_checkins')
        .select('id')
        .in('project_id', projectIdArray)
        .gte('check_in_time', today.toISOString());

      if (checkinError) throw checkinError;

      const checkinIds = (todayCheckins || []).map(c => c.id);
      if (checkinIds.length === 0) {
        setBreakEvents([]);
        return;
      }

      // Step 2: Get breaks for those checkins
      const { data, error } = await supabase
        .from('shift_breaks')
        .select(`
          id,
          checkin_id,
          user_id,
          start_time,
          end_time,
          duration_minutes,
          user:user_profiles!user_id(id, first_name, last_name, display_name, avatar_url)
        `)
        .in('checkin_id', checkinIds)
        .order('start_time', { ascending: false })
        .limit(50);

      if (error) throw error;

      setBreakEvents((data || []).map(b => {
        const u = b.user || {};
        return {
          ...b,
          first_name: u.first_name,
          last_name: u.last_name,
          display_name: u.display_name,
          avatar_url: u.avatar_url,
        };
      }));
    } catch (err) {
      console.error('Error fetching break events:', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, isStackView]);

  // Batch fetch employee compliance stats
  const fetchEmployeeStatsBatch = useCallback(async (userIds) => {
    if (!projectId || !userIds || userIds.length === 0) return;

    setStatsLoading(true);

    try {
      const { data, error } = await supabase.rpc('get_checkin_employee_stats_batch', {
        p_project_id: projectId,
        p_user_ids: userIds
      });

      if (error) throw error;

      setEmployeeStats(data || {});
    } catch (err) {
      console.error('Error fetching employee stats batch:', err);
    } finally {
      setStatsLoading(false);
    }
  }, [projectId]);

  // Get employee stats for a specific user (from batch-loaded data)
  const getEmployeeStats = useCallback((userId) => {
    return employeeStats[userId] || null;
  }, [employeeStats]);

  // Refresh all data
  const refetch = useCallback(async () => {
    initialLoadDone.current = false;
    await fetchInitialData();
  }, [fetchInitialData]);

  // Refresh on-site users only (for after check-in/check-out)
  const refreshOnSiteUsers = useCallback(async () => {
    if (!projectId && !isStackView) return;

    try {
      // Compute effective IDs inline to avoid dependency issues
      let effectiveIds = null;
      if (isStackView) {
        effectiveIds = selectedSpaceFilter === 'all' ? projectIds : [selectedSpaceFilter];
      }
      const projectIdArray = isStackView ? effectiveIds : [projectId];

      const { data, error } = await supabase
        .from('site_checkins')
        .select(`
          *,
          user:user_profiles(id, first_name, last_name, display_name, avatar_url, department),
          project:projects(id, name)
        `)
        .in('project_id', projectIdArray)
        .is('check_out_time', null)
        .order('check_in_time', { ascending: false });

      if (error) throw error;

      setAllOnSiteUsers((data || []).map(flattenCheckIn));

      // Refresh break events for activity feed
      fetchBreakEvents();

      // Update gap analysis with new on-site users
      if (!isStackView && projectId) {
        fetchGapAnalysis((data || []).map(u => u.user_id));
        fetchEmployeeStatsBatch((data || []).map(u => u.user_id));
      }
    } catch (err) {
      console.error('Error refreshing on-site users:', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, isStackView, fetchGapAnalysis, fetchEmployeeStatsBatch]);

  // Manual check-out handler
  const handleCheckOut = useCallback(async (checkInId) => {
    try {
      const { error } = await supabase
        .from('site_checkins')
        .update({
          check_out_time: new Date().toISOString(),
          status: 'checked_out'
        })
        .eq('id', checkInId);

      if (error) throw error;

      toast.success('User checked out successfully');

      // Optimistic update: remove from on-site users
      setAllOnSiteUsers(prev => prev.filter(u => u.id !== checkInId));

      // Refresh to get updated data
      await refreshOnSiteUsers();
    } catch (err) {
      console.error('Error checking out user:', err);
      toast.error('Failed to check out user');
    }
  }, [refreshOnSiteUsers]);

  // Manual check-in handler
  const handleCheckIn = useCallback(async (users, notes = '') => {
    if (!projectId || isStackView) {
      toast.error('Manual check-in is not available in stack view');
      return false;
    }

    // Filter out users already checked in
    const usersToCheckIn = users.filter(user =>
      !allOnSiteUsers.some(c => c.user_id === user.id)
    );

    if (usersToCheckIn.length === 0) {
      toast.error('All selected users are already checked in');
      return false;
    }

    try {
      const checkInsToCreate = usersToCheckIn.map(user => ({
        project_id: projectId,
        user_id: user.id,
        department: user.department || null,
        check_in_notes: notes || null,
        status: 'checked_in'
      }));

      const { data, error } = await supabase
        .from('site_checkins')
        .insert(checkInsToCreate)
        .select();

      if (error) throw error;

      const successCount = data?.length || usersToCheckIn.length;
      if (successCount === 1) {
        toast.success(`${usersToCheckIn[0].display_name || usersToCheckIn[0].first_name} checked in successfully`);
      } else {
        toast.success(`${successCount} employees checked in successfully`);
      }

      // Refresh data
      await refreshOnSiteUsers();
      return true;
    } catch (err) {
      console.error('Error creating manual check-in:', err);
      toast.error('Failed to check in users');
      return false;
    }
  }, [projectId, isStackView, allOnSiteUsers, refreshOnSiteUsers]);

  // Create site location if it doesn't exist
  const createSiteLocation = useCallback(async () => {
    if (!projectId || !project) return null;

    try {
      const qrCodeData = `site-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const { data: newSite, error } = await supabase
        .from('site_locations')
        .insert({
          project_id: projectId,
          name: project.name,
          address: project.location || null,
          qr_code_data: qrCodeData,
          is_active: true
        })
        .select()
        .single();

      if (error) throw error;

      setSiteLocation(newSite);
      return newSite;
    } catch (err) {
      console.error('Error creating site location:', err);
      return null;
    }
  }, [projectId, project]);

  // Initial load effect - only runs on mount or when projectId/enabled changes
  useEffect(() => {
    if (!enabled) return;
    if (!projectId && !isStackView) return;

    // Only fetch if this is initial load or projectId actually changed
    const projectIdChanged = prevProjectIdRef.current !== projectId;
    if (!initialLoadDone.current || projectIdChanged) {
      prevProjectIdRef.current = projectId;
      fetchInitialData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, projectId, isStackView]);

  // Re-fetch gap analysis when department changes
  useEffect(() => {
    if (!initialLoadDone.current) return;
    if (isStackView || !projectId) return;

    // Only fetch if department actually changed
    if (prevDepartmentRef.current !== selectedDepartment) {
      prevDepartmentRef.current = selectedDepartment;
      fetchGapAnalysis();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDepartment, isStackView, projectId]);

  // Re-fetch when space filter changes in stack view
  useEffect(() => {
    if (!isStackView) return;
    if (!initialLoadDone.current) return;

    // Only fetch if filter actually changed
    if (prevSpaceFilterRef.current !== selectedSpaceFilter) {
      prevSpaceFilterRef.current = selectedSpaceFilter;
      fetchInitialData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSpaceFilter, isStackView]);

  return {
    // Data
    project,
    siteLocation,
    allOnSiteUsers,
    onSiteUsers,
    recentCheckIns: filteredCheckIns,
    activityFeed,
    departments,
    analyticsData,
    companyId,
    stats,
    totalEmployees,
    totalOnSite,

    // Gap analysis
    gapAnalysis,
    gapSummary,

    // Employee stats
    employeeStats,
    getEmployeeStats,

    // Loading states
    initialLoading,
    gapLoading,
    statsLoading,
    loading: initialLoading,

    // Actions
    handleCheckOut,
    handleCheckIn,
    createSiteLocation,

    // Refresh
    refetch,
    refreshOnSiteUsers,
    fetchGapAnalysis,
    fetchEmployeeStatsBatch,

    // Utilities
    getDepartmentEmployeeCount,
    getDepartmentOnSiteCount
  };
};
